import { ActionButton, Item, Menu, MenuTrigger, Picker } from '@adobe/react-spectrum';
import SortOrderDown from '@spectrum-icons/workflow/SortOrderDown';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useMediaQuery } from '@exchange-frontends/custom-hooks';

const Container = styled.span`
  &&&& {
    div {
      width: 100% !important;
    }
  }
`;

/**
 * Sort Dropdown component that renders as an icon on mobile sizes. By default it has the following items available: [mostrecent, title, partner].
 *
 * If you want to override these or add to them use customItems or additionalItems.
 * @component
 * @category Shared Components
 * @prop {string} [defaultSelectedKey='mostrecent'] The key that will be selected by default.
 * @prop {function} onSelectionChange This function will be called when the selected value has changed. It received as argument the key of the selcted item.
 * @prop {boolean} [mobileVersion] Use this if you want to change when the component renders its mobile version. By default it will do if max-width < 767px.
 * @prop {object[]} [customItems] Change the default items. Format: `[{key: 'item1key', name: 'Item 1'}]`
 * @prop {object[]} [additionalItems] Add to the default items. Format: `[{key: 'item1key', name: 'Item 1'}]`
 * @returns
 */
const SortDropdown = ({
  defaultSelectedKey,
  onSelectionChange,
  mobileVersion,
  customItems,
  additionalItems,
  ...rest
}) => {
  const mobileMediaQueryMatches = useMediaQuery('(max-width: 767px)');
  const isMobile = mobileVersion ?? mobileMediaQueryMatches;
  const [selectedKey, setSelectedKey] = useState(
    defaultSelectedKey ?? (customItems ? customItems[0].key : 'mostrecent')
  );

  const items = customItems ?? [
    { key: 'mostrecent', name: 'Most recent' },
    { key: 'title', name: 'Title (A-Z)' },
    { key: 'partner', name: 'Partner (A-Z)' },
    ...(additionalItems ?? []),
  ];

  useEffect(() => {
    if (defaultSelectedKey !== selectedKey) {
      setSelectedKey(defaultSelectedKey ?? (customItems ? customItems[0].key : 'mostrecent'));
    }
  }, [defaultSelectedKey]);

  const onSelectionChangeFunction = (key) => {
    const value = key instanceof Set ? key.values().next().value : key;
    setSelectedKey(value);
    onSelectionChange(value);
  };

  return (
    <>
      {isMobile ? (
        <MenuTrigger>
          <ActionButton isQuiet data-testid="sort-button">
            <SortOrderDown size="S" />
          </ActionButton>
          <Menu
            selectionMode="single"
            selectedKeys={[selectedKey]}
            onSelectionChange={onSelectionChangeFunction}
            {...rest}
          >
            {items.map(({ key, name }) => (
              <Item key={key}>{name}</Item>
            ))}
          </Menu>
        </MenuTrigger>
      ) : (
        <Container>
          <Picker
            isQuiet
            label="Sort by:"
            labelPosition="side"
            align="start"
            menuWidth="size-2000"
            selectedKey={selectedKey}
            onSelectionChange={onSelectionChangeFunction}
            {...rest}
          >
            {items.map(({ key, name }) => (
              <Item key={key}>{name}</Item>
            ))}
          </Picker>
        </Container>
      )}
    </>
  );
};

export default SortDropdown;
